// Storage Constants - Default values, templates, and configuration constants
// Split from storage.ts for maintainability
// NOTE: Use 'var' instead of 'const' for exported values to avoid TDZ errors
// when re-exported via 'export *' in storage.ts barrel file.

import type {
  UserPermissions,
  PermissionTemplate,
  AuditMetadata,
  UploadDestination,
} from './storage-types';

// Default permissions for new users
export var DEFAULT_USER_PERMISSIONS: UserPermissions = {
  tabs: {
    reports: true,
    settings: true,
    carInfo: true,
    trips: true,
    accounting: false, // Must be granted by admin
  },
  subtabs: {
    salary: true,
    shifts: true,
    terminalReports: true,
    travelExpenses: true,
    drivers: true,
    bankAccounts: true,
    templates: true,
    companyInfo: true,
    apiConfig: true,
    userManagement: false, // Only admin by default
    debugLogs: false, // Only super admin by default
    bankChecker: false, // Must be granted by admin
    reportUpload: false, // Must be granted by admin
    accountingSettings: false, // Must be granted by admin
  },
  actions: {
    canEdit: true,
    canDelete: true,
    canExport: true,
    canImport: true,
  },
  notifications: {
    supportTickets: true,
    reportGenerated: true,
    systemAlerts: true,
    editChanges: false,
  },
  compliance: {
    viewDeviations: false,
    manageDeviations: false,
    viewComplaints: false,
    manageComplaints: false,
    respondToComplaints: false,
    viewRiskAssessment: false,
    manageRiskAssessment: false,
    approveRiskAssessment: false,
    viewTraining: false,
    manageTraining: false,
    viewManagementReview: false,
    conductManagementReview: false,
    signManagementReview: false,
    generateAuditPackage: false,
    viewLoyveHMS: false,
    manageLoyveHMS: false,
    viewPricing: false,
    managePricing: false,
  },
};

// System Permission Templates - Built-in templates
export var SYSTEM_PERMISSION_TEMPLATES: Omit<PermissionTemplate, keyof AuditMetadata>[] = [
  {
    id: 'admin_full',
    name: 'Admin - Full Access',
    description: 'Complete system access including user management',
    isSystemTemplate: true,
    permissions: {
      tabs: { reports: true, settings: true, carInfo: true, trips: true, accounting: true },
      subtabs: {
        salary: true,
        shifts: true,
        terminalReports: true,
        travelExpenses: true,
        drivers: true,
        bankAccounts: true,
        templates: true,
        companyInfo: true,
        apiConfig: true,
        userManagement: true, // Admin only
        debugLogs: false, // Super admin only by default
        bankChecker: true,
        reportUpload: true,
        accountingSettings: true,
      },
      actions: { canEdit: true, canDelete: true, canExport: true, canImport: true },
      notifications: { supportTickets: true, reportGenerated: true, systemAlerts: true, editChanges: true },
      compliance: {
        viewDeviations: true, manageDeviations: true,
        viewComplaints: true, manageComplaints: true, respondToComplaints: true,
        viewRiskAssessment: true, manageRiskAssessment: true, approveRiskAssessment: true,
        viewTraining: true, manageTraining: true,
        viewManagementReview: true, conductManagementReview: true, signManagementReview: true,
        generateAuditPackage: true,
        viewLoyveHMS: true, manageLoyveHMS: true,
        viewPricing: true, managePricing: true,
      },
    },
  },
  {
    id: 'owner_standard',
    name: 'Owner - Standard',
    description: 'Standard access for vehicle owners',
    isSystemTemplate: true,
    permissions: {
      tabs: { reports: true, settings: true, carInfo: true, trips: true, accounting: true },
      subtabs: {
        salary: true,
        shifts: true,
        terminalReports: true,
        travelExpenses: true,
        drivers: true,
        bankAccounts: true,
        templates: true,
        companyInfo: true,
        apiConfig: false,
        userManagement: false,
        debugLogs: false,
        bankChecker: true,
        reportUpload: true,
        accountingSettings: true,
      },
      actions: { canEdit: true, canDelete: true, canExport: true, canImport: true },
      notifications: { supportTickets: true, reportGenerated: true, systemAlerts: true, editChanges: true },
      compliance: {
        viewDeviations: true, manageDeviations: true,
        viewComplaints: true, manageComplaints: true, respondToComplaints: true,
        viewRiskAssessment: true, manageRiskAssessment: true, approveRiskAssessment: false,
        viewTraining: true, manageTraining: true,
        viewManagementReview: true, conductManagementReview: true, signManagementReview: true,
        generateAuditPackage: true,
        viewLoyveHMS: true, manageLoyveHMS: true,
        viewPricing: true, managePricing: true,
      },
    },
  },
  {
    id: 'accountant_standard',
    name: 'Accountant - Standard',
    description: 'Financial reporting access for accountants',
    isSystemTemplate: true,
    permissions: {
      tabs: { reports: true, settings: true, carInfo: false, trips: false, accounting: true },
      subtabs: {
        salary: true,
        shifts: true,
        terminalReports: true,
        travelExpenses: true,
        drivers: false,
        bankAccounts: true,
        templates: true,
        companyInfo: true,
        apiConfig: false,
        userManagement: false,
        debugLogs: false,
        bankChecker: true,
        reportUpload: false,
        accountingSettings: false,
      },
      actions: { canEdit: true, canDelete: false, canExport: true, canImport: false },
      notifications: { supportTickets: true, reportGenerated: true, systemAlerts: true, editChanges: false },
      compliance: {
        viewDeviations: true, manageDeviations: false,
        viewComplaints: false, manageComplaints: false, respondToComplaints: false,
        viewRiskAssessment: false, manageRiskAssessment: false, approveRiskAssessment: false,
        viewTraining: false, manageTraining: false,
        viewManagementReview: false, conductManagementReview: false, signManagementReview: false,
        generateAuditPackage: false,
        viewLoyveHMS: false, manageLoyveHMS: false,
        viewPricing: false, managePricing: false,
      },
    },
  },
  {
    id: 'driver_basic',
    name: 'Driver - Basic',
    description: 'View-only access for drivers',
    isSystemTemplate: true,
    permissions: {
      tabs: { reports: true, settings: false, carInfo: false, trips: true, accounting: false },
      subtabs: {
        salary: true,
        shifts: true,
        terminalReports: false,
        travelExpenses: false,
        drivers: false,
        bankAccounts: false,
        templates: false,
        companyInfo: false,
        apiConfig: false,
        userManagement: false,
        debugLogs: false,
        bankChecker: false,
        reportUpload: false,
        accountingSettings: false,
      },
      actions: { canEdit: false, canDelete: false, canExport: true, canImport: false },
      notifications: { supportTickets: false, reportGenerated: false, systemAlerts: true, editChanges: false },
      compliance: {
        viewDeviations: false, manageDeviations: false,
        viewComplaints: false, manageComplaints: false, respondToComplaints: false,
        viewRiskAssessment: false, manageRiskAssessment: false, approveRiskAssessment: false,
        viewTraining: false, manageTraining: false,
        viewManagementReview: false, conductManagementReview: false, signManagementReview: false,
        generateAuditPackage: false,
        viewLoyveHMS: false, manageLoyveHMS: false,
        viewPricing: false, managePricing: false,
      },
    },
  },
];

// Gemini 2.0 Flash pricing (per 1M tokens)
export var GEMINI_PRICING = {
  inputPer1M: 0.10, // $0.10 per 1M input tokens
  outputPer1M: 0.40, // $0.40 per 1M output tokens
  freeRpmLimit: 15, // Free tier: 15 requests per minute
  usdToNok: 10.0, // Approximate USD to NOK exchange rate
};

// Default upload destinations
export var DEFAULT_UPLOAD_DESTINATIONS: UploadDestination[] = [
  { reportType: 'shifts', target: 'bilagsforing', enabled: true },
  { reportType: 'terminal', target: 'bilagsforing', enabled: true },
  { reportType: 'travel', target: 'bilagsforing', enabled: true },
  { reportType: 'lonn', target: 'shared_documents', enabled: true },
];

// App location tree for error reporting
export var APP_LOCATION_TREE: Record<string, Record<string, string[]>> = {
  'Oversikt': { 'Dashboard': ['Statistikk', 'Snarvegar', 'Startguide'] },
  'Rapportar': {
    'Skiftrapportar': ['Filopplasting', 'Rapportgenerering', 'PDF-eksport', 'Kolonneval', 'Sjåførval'],
    'Lønnsrapportar': ['Filopplasting', 'Kontantredigering', 'PDF-generering', 'Sjåførval', 'Utrekning'],
    'Terminalrapportar': ['Filopplasting', 'Rapportgenerering', 'PDF-eksport'],
    'Reiseregning': ['Skjemautfylling', 'Utgiftspostar', 'Vedlegg', 'PDF-generering'],
  },
  'Bil info': {
    'Taxi4u API': ['Biloversikt', 'Statusfilter', 'Oppdatering'],
    'Bilregister': ['Legg til bil', 'Rediger bil', 'Auto-deteksjon'],
  },
  'Turer': { 'Turar': ['Kommande', 'Fullførte', 'Innlogging'] },
  'Regnskap': { 'Banksjekk': ['CSV-opplasting', 'Avstemming', 'Filter', 'Samandrag'] },
  'Innstillingar': {
    'Generelt': ['Tema', 'Språk', 'Google-innlogging'],
    'Admin': ['Brukaradministrasjon', 'Selskapsinfo', 'Sjåførar', 'Bankkonto', 'Malar', 'Rekneskapinnstillingar'],
    'Superadmin': ['PowerOffice-tilkopling', 'Sentralregister', 'Rettsmalar'],
    'Feilhandtering': ['Feilmeldingar', 'Konsolloggar'],
  },
};
